import { useEffect, useState, useCallback, useRef } from 'react';
import { getBrandingPrimaryColor } from '@/hooks/useBrandingOverrides';

const MAX_INIT_RETRIES = 3;
const DIDOMI_READY_TIMEOUT_MS = 10000;
// The SDK can become ready well after the 10s consent fallback fires (slow
// networks, main thread busy). Keep polling longer so a late-ready SDK still
// gets its listeners registered and consent re-evaluated within the session.
const DIDOMI_READY_POLL_INTERVAL_MS = 500;
const DIDOMI_READY_POLL_MAX_MS = 60000;

declare global {
  interface Window {
    Didomi: any;
    didomiOnReady: Array<(Didomi: any) => void>;
    didomiConfig: any;
  }
}

export interface ConsentStatus {
  isReady: boolean;
  isConsentRequired: boolean;
  hasAnalyticsConsent: boolean;
  hasVideoConsent: boolean;
  hasAdvertisingConsent: boolean;
}

export interface ATTStatusInput {
  isLoading: boolean;
  isTrackingAuthorized: boolean;
  status: string;
}

// The purpose IDs of notice Ec8Wc23H are configured server-side in the Didomi
// console and may not match the hardcoded lists below (see
// Docs/didomi-consent-purpose-id-fix.md). When the user accepted every purpose
// ("Agree to all": nothing disabled), grant consent regardless of ID matching
// so a notice reconfiguration can never silently disable tracking for users
// who accepted everything.
function computeConsentFromPurposes(enabled: string[], disabled: string[]): {
  hasAnalyticsConsent: boolean;
  hasVideoConsent: boolean;
  hasAdvertisingConsent: boolean;
} {
  const acceptedAll = enabled.length > 0 && disabled.length === 0;

  const hasAnalyticsConsent = acceptedAll
    || enabled.includes('cookies')
    || enabled.includes('analytics')
    || enabled.includes('measure_ad_performance')
    || enabled.includes('measure_content_performance');
  const hasVideoConsent = acceptedAll
    || enabled.includes('content_personalization')
    || enabled.includes('create_ads_profile')
    || enabled.includes('select_personalized_content');
  const hasAdvertisingConsent = acceptedAll
    || enabled.includes('advertising_personalization')
    || enabled.includes('select_personalized_ads')
    || enabled.includes('select_basic_ads');

  if (enabled.length > 0 && !hasAnalyticsConsent) {
    console.error(
      '[Didomi Web] ⚠️ Purpose-ID mismatch: user enabled purposes but none match the analytics list.',
      'Enabled:', JSON.stringify(enabled), 'Disabled:', JSON.stringify(disabled)
    );
  }

  return { hasAnalyticsConsent, hasVideoConsent, hasAdvertisingConsent };
}

// The Didomi WEB SDK exposes no `isReady` property or method — that API only
// exists on the mobile SDKs. Probing `window.Didomi.isReady` therefore always
// yielded false, which kept every readiness check in this hook permanently
// "not ready" and left the 10s timeout fallback as the only path that ever
// unblocked tracking. Web readiness is announced through the `didomiOnReady`
// callback queue; as a safety net, the SDK is also considered ready once the
// methods this hook actually consumes exist.
let didomiAnnouncedReady = false;

function markDidomiSdkReady(): void {
  didomiAnnouncedReady = true;
}

function isDidomiSdkReady(): boolean {
  if (typeof window === 'undefined' || !window.Didomi) return false;
  if (didomiAnnouncedReady) return true;
  return typeof window.Didomi.getUserStatus === 'function'
    && (typeof window.Didomi.isConsentRequired === 'function'
      || typeof window.Didomi.shouldConsentBeCollected === 'function');
}

// Web version doesn't use ATT, but accepts the parameter for API consistency
export function useDidomi(attStatus?: ATTStatusInput) {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>({
    isReady: false,
    isConsentRequired: false,
    hasAnalyticsConsent: false,
    hasVideoConsent: false,
    hasAdvertisingConsent: false,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const retryCountRef = useRef(0);
  const listenersRegisteredRef = useRef(false);
  // Listeners are registered once but must always invoke the latest callback.
  const updateConsentStatusRef = useRef<() => void>(() => {});

  // Register the listeners idempotently from every code path that detects the
  // SDK (didomiOnReady, polling, timeout): whichever path observes the SDK
  // first wires them, so a consent given after the initial status read is
  // always observed.
  const registerDidomiListeners = useCallback(() => {
    if (listenersRegisteredRef.current) return;
    if (typeof window === 'undefined' || !window.Didomi || typeof window.Didomi.on !== 'function') return;
    listenersRegisteredRef.current = true;

    window.Didomi.on('consent.changed', () => {
      console.log('[Didomi Web] 🔔 Consent changed');
      updateConsentStatusRef.current();
    });

    window.Didomi.on('notice.shown', () => {
      console.log('[Didomi Web] 🔔 Notice shown');
    });

    window.Didomi.on('notice.hidden', () => {
      console.log('[Didomi Web] 🔔 Notice hidden');
      updateConsentStatusRef.current();
    });

    console.log('[Didomi Web] Event listeners registered');
  }, []);

  useEffect(() => {
    if (consentStatus.isReady) return;

    const timer = setTimeout(() => {
      registerDidomiListeners();

      setConsentStatus(prev => {
        if (prev.isReady) return prev;

        const didomiLoaded = typeof window !== 'undefined' && window.Didomi;

        // Policy: no CMP → no tracking, even where consent is not legally
        // required. Without the SDK we cannot determine consent, so deny
        // everything; if the SDK loads later, the extended polling and
        // late-ready paths re-evaluate the real consent state.
        if (!didomiLoaded) {
          console.warn(
            `[Didomi Web] Timeout: SDK not loaded after ${DIDOMI_READY_TIMEOUT_MS}ms — tracking disabled (no CMP, no tracking)`
          );
          return {
            isReady: true,
            isConsentRequired: false,
            hasAnalyticsConsent: false,
            hasVideoConsent: false,
            hasAdvertisingConsent: false,
          };
        }

        const consentRequired = typeof window.Didomi.isConsentRequired === 'function'
          ? window.Didomi.isConsentRequired()
          : window.Didomi.shouldConsentBeCollected?.() || false;

        let hasAnalyticsConsent = !consentRequired;
        let hasVideoConsent = !consentRequired;
        let hasAdvertisingConsent = false;

        if (consentRequired) {
          try {
            const userStatus = window.Didomi.getUserStatus?.();
            const enabledPurposes = userStatus?.purposes?.consent?.enabled || [];
            const disabledPurposes = userStatus?.purposes?.consent?.disabled || [];
            console.log('[Didomi Web] Timeout: reading stored consent, enabled purposes:', JSON.stringify(enabledPurposes));
            if (enabledPurposes.length > 0) {
              ({ hasAnalyticsConsent, hasVideoConsent, hasAdvertisingConsent } =
                computeConsentFromPurposes(enabledPurposes, disabledPurposes));
            }
          } catch (e) {
            console.warn('[Didomi Web] Timeout: failed to read user status:', e);
          }
        }

        console.warn(
          `[Didomi Web] Timeout: SDK not ready after ${DIDOMI_READY_TIMEOUT_MS}ms.`,
          `Didomi loaded: ${!!didomiLoaded}, consent required: ${consentRequired},`,
          `hasAnalyticsConsent: ${hasAnalyticsConsent}`
        );

        return {
          isReady: true,
          isConsentRequired: consentRequired,
          hasAnalyticsConsent,
          hasVideoConsent,
          hasAdvertisingConsent,
        };
      });
    }, DIDOMI_READY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [consentStatus.isReady, registerDidomiListeners]);

  const updateConsentStatus = useCallback(() => {
    if (!window.Didomi) {
      return;
    }

    try {
      const isReady = isDidomiSdkReady();

      // Deny by default: consent is only granted below from an explicit,
      // successful read of the SDK state — never assumed. When the state
      // cannot be read, everything stays denied and a later path (listeners,
      // polling, 10s timeout) re-evaluates.
      let isConsentRequired = true;
      let hasAnalyticsConsent = false;
      let hasVideoConsent = false;
      let hasAdvertisingConsent = false;

      if (typeof window.Didomi.isConsentRequired === 'function') {
        isConsentRequired = window.Didomi.isConsentRequired();
      } else if (typeof window.Didomi.shouldConsentBeCollected === 'function') {
        isConsentRequired = window.Didomi.shouldConsentBeCollected();
      }

      if (!isConsentRequired) {
        // The CMP is present and states consent is not required for this geo:
        // analytics/video are allowed without user action. Advertising stays
        // opt-in only (same as the 10s timeout fallback).
        hasAnalyticsConsent = true;
        hasVideoConsent = true;
      } else if (typeof window.Didomi.getUserStatus === 'function') {
        const userStatus = window.Didomi.getUserStatus();
        const enabledPurposes = userStatus?.purposes?.consent?.enabled || [];
        const disabledPurposes = userStatus?.purposes?.consent?.disabled || [];
        console.log('[Didomi Web] Enabled purposes:', JSON.stringify(enabledPurposes));
        ({ hasAnalyticsConsent, hasVideoConsent, hasAdvertisingConsent } =
          computeConsentFromPurposes(enabledPurposes, disabledPurposes));
      }

      setConsentStatus({
        isReady,
        isConsentRequired,
        hasAnalyticsConsent,
        hasVideoConsent,
        hasAdvertisingConsent,
      });

      /*console.log('[Didomi Web] Consent status updated:', {
        isReady,
        isConsentRequired,
        hasAnalyticsConsent,
        hasVideoConsent,
        hasAdvertisingConsent,
      });*/
    } catch (error) {
      console.error('[Didomi Web] Error updating consent status:', error);
    }
  }, []);

  updateConsentStatusRef.current = updateConsentStatus;

  const loadDidomiScript = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isInitialized) {
      console.log('[Didomi Web] ⚠️ Already initialized, skipping duplicate initialization');
      return;
    }

    if (window.Didomi) {
      console.log('[Didomi Web] Already loaded');
      if (isDidomiSdkReady()) {
        registerDidomiListeners();
        updateConsentStatus();
        return;
      }
      // SDK object exists but not ready yet - register callback and poll
      window.didomiOnReady = window.didomiOnReady || [];
      window.didomiOnReady.push(() => {
        console.log('[Didomi Web] 🟩 SDK ready (via late didomiOnReady)');
        markDidomiSdkReady();
        registerDidomiListeners();
        updateConsentStatus();
      });
      const pollExisting = setInterval(() => {
        if (isDidomiSdkReady()) {
          clearInterval(pollExisting);
          registerDidomiListeners();
          updateConsentStatus();
        }
      }, DIDOMI_READY_POLL_INTERVAL_MS);
      setTimeout(() => clearInterval(pollExisting), DIDOMI_READY_POLL_MAX_MS);
      setIsInitialized(true);
      return;
    }

    console.log('[Didomi Web] 🔵 Starting Didomi SDK initialization...');
    setIsInitialized(true);

    const primaryColor = getBrandingPrimaryColor() || '#A855F7';
    const didomiApiKey = process.env.EXPO_PUBLIC_DIDOMI_API_KEY || '997a47aa-b384-4386-ad9c-16910dff5088';
    const didomiNoticeId = process.env.EXPO_PUBLIC_DIDOMI_NOTICE_ID || 'Ec8Wc23H';

    window.didomiConfig = {
      app: {
        apiKey: didomiApiKey,
        noticeId: didomiNoticeId,
        delayBeforeShowingNotice: 3000,
      },
      notice: {
        enabledByDefault: true,
        position: 'bottom',
        showCloseIcon: false,
      },
      theme: {
        color: primaryColor,
        buttons: {
          regularButtons: {
            backgroundColor: primaryColor,
            textColor: '#FFFFFF',
            borderRadius: '8px',
          },
          highlightButtons: {
            backgroundColor: primaryColor,
            textColor: '#FFFFFF',
            borderRadius: '8px',
          },
        },
        notice: {
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          textColor: '#000000',
          linkColor: primaryColor,
        },
      },
      languages: {
        enabled: ['fr', 'en', 'es', 'it', 'ar', 'de', 'nl', 'ro'],
        default: 'fr',
      },
    };

    const style = document.createElement('style');
    style.innerHTML = `
      #didomi-host {
        --didomi-primary-color: ${primaryColor} !important;
      }

      .didomi-popup-container,
      .didomi-notice-popup,
      #didomi-notice {
        max-width: 100% !important;
        width: 100% !important;
        border-radius: 0 !important;
        padding: 10px 16px !important;
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)) !important;
        box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.1) !important;
        background-color: #FFFFFF !important;
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        transform: none !important;
        margin: 0 !important;
        max-height: 45vh !important;
        overflow-y: auto !important;
      }

      .didomi-popup-backdrop,
      .didomi-screen-medium {
        background: rgba(0, 0, 0, 0.6) !important;
        backdrop-filter: blur(2px) !important;
      }

      body.didomi-popup-open {
        overflow: hidden !important;
      }

      .didomi-popup-notice-text {
        font-size: 11px !important;
        line-height: 1.3 !important;
        color: #333333 !important;
        margin: 0 !important;
        max-height: 80px !important;
        overflow-y: auto !important;
      }

      .didomi-popup-notice-text a,
      .didomi-popup-notice-text-link {
        color: ${primaryColor} !important;
        font-weight: 600 !important;
        text-decoration: underline !important;
      }

      .didomi-buttons,
      .didomi-notice-buttons {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 6px !important;
        margin-top: 10px !important;
        width: 100% !important;
      }

      .didomi-button,
      .didomi-buttons button,
      .didomi-notice-view-partners-button,
      button[id*="didomi"] {
        border: none !important;
        padding: 8px 16px !important;
        border-radius: 8px !important;
        font-size: 13px !important;
        cursor: pointer !important;
        text-align: center !important;
        margin: 0 !important;
        transition: opacity 0.2s !important;
        width: auto !important;
      }

      .didomi-button:hover,
      .didomi-buttons button:hover {
        opacity: 0.85 !important;
      }

      .didomi-button.didomi-button-agree,
      .didomi-button-highlight,
      .didomi-agree-button,
      button[id*="agree"],
      button[id*="accept"] {
        background-color: ${primaryColor} !important;
        color: #FFFFFF !important;
        font-weight: 600 !important;
        order: 1 !important;
        border: none !important;
        width: 100% !important;
      }

      .didomi-button.didomi-button-disagree,
      .didomi-button-standard.didomi-disagree-button,
      .didomi-disagree-button,
      .didomi-continue-without-agreeing,
      button[id*="disagree"],
      button[id*="refuse"],
      button[id*="continue"] {
        background-color: transparent !important;
        color: #555555 !important;
        font-weight: 400 !important;
        order: 3 !important;
        text-decoration: underline !important;
        border: none !important;
        padding: 4px 12px !important;
        font-size: 12px !important;
        width: auto !important;
      }

      .didomi-button.didomi-button-information,
      .didomi-button-highlight.didomi-button-information,
      .didomi-notice-learn-more-button,
      button[id*="learn-more"],
      button[id*="notice-learn-more"],
      button[id*="manage"] {
        background-color: transparent !important;
        color: #555555 !important;
        font-weight: 400 !important;
        order: 2 !important;
        text-decoration: underline !important;
        font-size: 12px !important;
        padding: 4px 12px !important;
        width: auto !important;
      }

      .didomi-notice-buttons-bottom,
      .didomi-buttons-container {
        border-top: none !important;
        margin-top: 8px !important;
        padding-top: 0 !important;
      }

      .didomi-popup-notice-logo {
        margin-bottom: 8px !important;
        max-width: 120px !important;
      }

      .didomi-popup-title {
        font-size: 14px !important;
        font-weight: 600 !important;
        margin-bottom: 4px !important;
        color: #000000 !important;
      }

      @media (max-width: 768px) {
        .didomi-popup-container,
        .didomi-notice-popup,
        #didomi-notice {
          max-width: 100% !important;
          width: 100% !important;
          border-radius: 0 !important;
          margin: 0 !important;
          padding: 8px 12px !important;
          padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px)) !important;
          left: 0 !important;
          right: 0 !important;
          transform: none !important;
          max-height: 40vh !important;
        }

        .didomi-popup-notice-text {
          font-size: 10px !important;
          line-height: 1.25 !important;
          max-height: 70px !important;
        }

        .didomi-button.didomi-button-agree,
        .didomi-button-highlight,
        .didomi-agree-button,
        button[id*="agree"],
        button[id*="accept"] {
          padding: 7px 10px !important;
          font-size: 12px !important;
        }
      }
    `;
    document.head.appendChild(style);

    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(() => {
      console.log('[Didomi Web] 🟩 SDK ready (App V2 WebView)');
      markDidomiSdkReady();
      registerDidomiListeners();
      updateConsentStatus();
    });

    const script = document.createElement('script');
    script.src = `https://sdk.privacy-center.org/${didomiApiKey}/loader.js`;
    script.async = true;
    script.onload = () => {
      console.log('[Didomi Web] Script loaded');
      // Poll for SDK ready state (didomiOnReady callback may not fire if SDK is already ready).
      // Polling outlives the 10s consent fallback: if the SDK becomes ready late, we still
      // need its listeners registered and the real consent state applied.
      const pollInterval = setInterval(() => {
        if (isDidomiSdkReady()) {
          clearInterval(pollInterval);
          console.log('[Didomi Web] SDK ready detected via polling, updating consent');
          registerDidomiListeners();
          updateConsentStatus();
        }
      }, DIDOMI_READY_POLL_INTERVAL_MS);
      setTimeout(() => clearInterval(pollInterval), DIDOMI_READY_POLL_MAX_MS);
    };
    script.onerror = (error) => {
      console.error('[Didomi Web] Script load error:', error);
      retryCountRef.current++;
      if (retryCountRef.current < MAX_INIT_RETRIES) {
        // Re-triggers loadDidomiScript via the mount effect. Consent status is left
        // untouched so the retry (or the 10s timeout fallback) decides it.
        console.log(`[Didomi Web] Retrying script load (attempt ${retryCountRef.current + 1}/${MAX_INIT_RETRIES})`);
        setIsInitialized(false);
        return;
      }
      // Script permanently unreachable (ad-blocker, network). Policy: no CMP →
      // no tracking, even where consent is not legally required. Same conclusion
      // as the 10s timeout fallback when the SDK is absent.
      console.warn('[Didomi Web] Didomi unavailable after retries — tracking disabled (no CMP, no tracking)');
      setConsentStatus({
        isReady: true,
        isConsentRequired: false,
        hasAnalyticsConsent: false,
        hasVideoConsent: false,
        hasAdvertisingConsent: false,
      });
    };

    document.head.insertBefore(script, document.head.firstChild);
  }, [updateConsentStatus, isInitialized]);

  const showPreferences = useCallback(() => {
    console.log('[Didomi Web] 🔔 showPreferences() called');

    if (!window.Didomi) {
      console.log('[Didomi Web] ❌ SDK not available');
      return;
    }

    try {
      console.log('[Didomi Web] 🟦 Attempting to show preferences UI...');
      window.Didomi.preferences.show();
      console.log('[Didomi Web] 🟩 showPreferences() completed successfully');
    } catch (error) {
      console.error('[Didomi Web] ❌ Error showing preferences:', error);
    }
  }, []);

  const getUserConsentStatus = useCallback((purposeId: string): boolean => {
    if (!window.Didomi) {
      return false;
    }

    if (!isDidomiSdkReady()) {
      return false;
    }

    try {
      const userStatus = window.Didomi.getUserStatus?.();
      if (userStatus?.purposes?.consent?.enabled) {
        return userStatus.purposes.consent.enabled.includes(purposeId);
      }
      return false;
    } catch (error) {
      console.error(`[Didomi Web] Error getting consent status for ${purposeId}:`, error);
      return false;
    }
  }, []);

  const setUserConsent = useCallback((purposeId: string, enabled: boolean) => {
    if (!window.Didomi) {
      return;
    }

    try {
      if (typeof window.Didomi.setUserStatus === 'function') {
        const currentStatus = window.Didomi.getUserStatus?.();
        const currentEnabled = currentStatus?.purposes?.consent?.enabled || [];
        const currentDisabled = currentStatus?.purposes?.consent?.disabled || [];

        let newEnabled: string[];
        let newDisabled: string[];

        if (enabled) {
          newEnabled = [...new Set([...currentEnabled, purposeId])];
          newDisabled = currentDisabled.filter((id: string) => id !== purposeId);
        } else {
          newEnabled = currentEnabled.filter((id: string) => id !== purposeId);
          newDisabled = [...new Set([...currentDisabled, purposeId])];
        }

        window.Didomi.setUserStatus({
          purposes: { consent: { enabled: newEnabled, disabled: newDisabled } },
          vendors: {},
        });
      }
      updateConsentStatus();
    } catch (error) {
      console.error(`[Didomi Web] Error setting consent for ${purposeId}:`, error);
    }
  }, [updateConsentStatus]);

  useEffect(() => {
    // Defer loading the Didomi SDK (and its ~17KB consent stylesheet) until the
    // browser is idle, i.e. after the first paint. This keeps that CSS — almost
    // all of it unused above the fold — out of the critical rendering path
    // (PageSpeed "reduce unused CSS"). The notice itself already waits 3s
    // (delayBeforeShowingNotice) and analytics stays gated on `didomi.isReady`,
    // so a sub-second deferral changes neither the consent UX nor tracking.
    if (typeof window === 'undefined') {
      loadDidomiScript();
      return;
    }

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => loadDidomiScript(), { timeout: 2500 });
      return () => window.cancelIdleCallback?.(handle);
    }

    // Fallback for browsers without requestIdleCallback (e.g. older Safari).
    const timer = setTimeout(() => loadDidomiScript(), 1200);
    return () => clearTimeout(timer);
  }, [loadDidomiScript]);

  useEffect(() => {
    if (!consentStatus.isReady || !isInitialized) return;

    if (isDidomiSdkReady()) {
      console.log('[Didomi Web] Late SDK ready detected, re-evaluating consent');
      registerDidomiListeners();
      updateConsentStatus();
    }
  }, [consentStatus.isReady, isInitialized, updateConsentStatus, registerDidomiListeners]);

  return {
    ...consentStatus,
    showPreferences,
    getUserConsentStatus,
    setUserConsent,
    updateConsentStatus,
  };
}
